<template>
    <div class="mt-5 rounded bg-slate-50 px-4 py-5">
        <div class="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div class="mb-4 sm:mb-0">
                <h3 class="text-lg font-semibold text-slate-800">Audience Demographics</h3>
                <p class="text-sm text-slate-600">Get insights into {{ superview.normalized.display_name }}'s audience</p>
            </div>
            <button
                class="btn flex items-center bg-indigo-500 px-3 py-2 text-sm text-white hover:bg-indigo-600"
                @click="$emit('view-full-audience')"
            >
                <AppIcon name="chart-pie" class="mr-2 h-4 w-4" />
                View Full Analysis
            </button>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="relative">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3 opacity-40">
                <div v-for="i in 3" :key="i" class="rounded border border-slate-200 bg-white p-4">
                    <div class="h-4 w-24 animate-pulse rounded bg-slate-200 mb-3"></div>
                    <div class="space-y-2">
                        <div class="h-3 w-full animate-pulse rounded bg-slate-100"></div>
                        <div class="h-3 w-3/4 animate-pulse rounded bg-slate-100"></div>
                        <div class="h-3 w-1/2 animate-pulse rounded bg-slate-100"></div>
                    </div>
                </div>
            </div>
            <div class="absolute inset-0 flex items-center justify-center">
                <div class="w-full max-w-sm px-4">
                    <div class="text-center text-sm">
                        <p v-if="progressMessage">{{ progressMessage }}</p>
                        <p v-else>Analyzing audience demographics...</p>
                    </div>
                    <div class="mt-3 h-2.5 w-full rounded bg-gray-200">
                        <div class="h-2.5 rounded bg-indigo-500 transition-all duration-300" :style="{ width: `${progress}%` }"></div>
                    </div>
                    <p v-if="progress > 0" class="mt-1 text-xs text-indigo-600 text-right">{{ Math.round(progress) }}%</p>
                </div>
            </div>
        </div>

        <!-- Preview Content -->
        <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-3">
            <!-- Top Countries -->
            <div class="rounded border border-slate-200 bg-white p-4">
                <h4 class="mb-3 text-sm font-semibold text-slate-700">Top Countries</h4>
                <div v-if="topCountries.length > 0" class="space-y-1">
                    <div
                        v-for="country in topCountries.slice(0, 3)"
                        :key="country.key"
                        class="relative py-1 px-2 rounded"
                    >
                        <div
                            class="absolute inset-0 bg-indigo-100 rounded"
                            aria-hidden="true"
                            :style="{
                                width: `${$numeral(country.percentage / 100).format('0%')}`,
                            }"
                        />
                        <div class="relative flex items-center justify-between text-sm">
                            <div class="flex items-center">
                                <span class="mr-2 font-medium">{{ country.value }}</span>
                                <span class="text-slate-500">{{ getCountryName(country.value) }}</span>
                            </div>
                            <span class="font-medium text-slate-700">{{ formatPercentage(country.percentage) }}</span>
                        </div>
                    </div>
                    <div v-if="demographics?.countries?.length > 3" class="pt-1 text-xs text-slate-500">
                        +{{ demographics.countries.length - 3 }} more countries
                    </div>
                </div>
                <div v-else class="text-sm text-slate-500 italic">
                    Click "View Full Analysis" to see country breakdown
                </div>
            </div>

            <!-- Gender Distribution -->
            <div class="rounded border border-slate-200 bg-white p-4">
                <h4 class="mb-3 text-sm font-semibold text-slate-700">Gender Split</h4>
                <div v-if="demographics?.gender_distribution" class="space-y-1">
                    <div
                        v-for="(genderData, gender) in demographics.gender_distribution"
                        :key="gender"
                        class="relative py-1 px-2 rounded"
                    >
                        <div
                            class="absolute inset-0 bg-emerald-100 rounded"
                            aria-hidden="true"
                            :style="{
                                width: `${$numeral(genderData.percentage / 100).format('0%')}`,
                            }"
                        />
                        <div class="relative flex items-center justify-between text-sm">
                            <span class="capitalize">{{ gender === 'non_binary' ? 'Non-Binary' : gender }}</span>
                            <span class="font-medium text-slate-700">{{ formatPercentage(genderData.percentage) }}</span>
                        </div>
                    </div>
                </div>
                <div v-else class="text-sm text-slate-500 italic">
                    Gender insights available in full analysis
                </div>
            </div>

            <!-- Age Highlights -->
            <div class="rounded border border-slate-200 bg-white p-4">
                <h4 class="mb-3 text-sm font-semibold text-slate-700">Age Insights</h4>
                <div v-if="topAgeRanges.length > 0" class="space-y-1">
                    <div
                        v-for="age in topAgeRanges.slice(0, 3)"
                        :key="age.value"
                        class="relative py-1 px-2 rounded"
                    >
                        <div
                            class="absolute inset-0 bg-sky-100 rounded"
                            aria-hidden="true"
                            :style="{
                                width: `${$numeral(age.percentage / 100).format('0%')}`,
                            }"
                        />
                        <div class="relative flex items-center justify-between text-sm">
                            <span>{{ age.value }}</span>
                            <span class="font-medium text-slate-700">{{ formatPercentage(age.percentage) }}</span>
                        </div>
                    </div>
                    <div v-if="demographics?.age_ranges?.length > 3" class="pt-1 text-xs text-slate-500">
                        +{{ demographics.age_ranges.length - 3 }} more age groups
                    </div>
                </div>
                <div v-else class="text-sm text-slate-500 italic">
                    Age demographics available in full report
                </div>
            </div>
        </div>

        <!-- Additional Insights Teaser -->
        <div v-if="!isLoading" class="mt-4 rounded border border-dashed border-slate-300 bg-slate-25 p-3">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div class="flex flex-wrap items-center text-sm text-slate-600">
                    <span class="flex items-center mr-4 mt-2 sm:mt-0">
                        <AppIcon name="globe" class="mr-1 h-3 w-3" />
                        Languages
                    </span>
                    <span class="flex items-center mr-4 mt-2 sm:mt-0">
                        <AppIcon name="laptop" class="mr-1 h-3 w-3" />
                        Devices
                    </span>
                    <span class="flex items-center mr-4 mt-2 sm:mt-0">
                        <AppIcon name="chart-line" class="mr-1 h-3 w-3" />
                        Interests
                    </span>
                    <span class="flex items-center mr-4 mt-2 sm:mt-0">
                        <AppIcon name="shield-alt" class="mr-1 h-3 w-3" />
                        Brand Safety
                    </span>
                </div>
                <button
                    class="mt-4 sm:mt-0 text-left text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    @click="$emit('view-full-audience')"
                >
                    See All Insights →
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'

export default {
    name: 'AudiencePreview',

    props: {
        superview: {
            type: Object,
            required: true,
        },
        demographics: {
            type: Object,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        progress: {
            type: Number,
            default: 0,
        },
        progressMessage: {
            type: String,
            default: '',
        },
    },

    emits: ['view-full-audience'],

    computed: {
        topCountries() {
            if (!this.demographics?.countries) return []
            return _.orderBy(this.demographics.countries, ['percentage'], ['desc']).slice(0, 4)
        },

        topAgeRanges() {
            if (!this.demographics?.age_ranges) return []
            return _.orderBy(this.demographics.age_ranges, ['percentage'], ['desc']).slice(0, 4)
        },
    },

    methods: {
        formatPercentage(percentage) {
            if (percentage > 1) {
                return this.$numeral(percentage / 100).format('0%')
            }
            return '<1%'
        },

        getCountryName(countryCode) {
            return this.$store.getters.getCountryFromCode(countryCode) || ''
        },
    },
}
</script>

